import isEmpty from 'lodash/isEmpty';
import * as d3 from 'd3';

export function reflectPeaks(peaks) {
    let hasMinValues = peaks.some(peak => peak < 0);
    if (!hasMinValues) {
        let reflectedPeaks = [];
        for (let i = 0, len = peaks.length; i < len; i++) {
            reflectedPeaks[2 * i] = peaks[i];
            reflectedPeaks[2 * i + 1] = -peaks[i];
        }
        return reflectedPeaks;
    }
    return peaks;
}

export function generateDynamicCircularSvgPathDArray(peaks, radius, density, height, width) {
    let arr = [];
    let leftPeaks = getLeftChannelPeaks(peaks);
    const deltaX = width / 2;
    const deltaY = height / 2;
    let deg = density / radius;
    let absMax = normalizePeaks(leftPeaks);
    leftPeaks.forEach((peak, index) => {
        arr.push(generateDynamicCircularSvgPathD(peak, index, deltaX, deltaY, radius, deg, absMax, height, width))
    })
    let minFracX = Math.min(...(arr.map((obj) => {return obj.fracX})));
    let minFracY = Math.min(...(arr.map((obj) => {return obj.fracY})));
    if (minFracX < 1) {
        arr.forEach(obj => {obj.x2 = obj.x1 < width/2 ? obj.x1 - minFracX*obj.distX : obj.x1 + minFracX*obj.distX});
    }
    if (minFracY < 1) {
        arr.forEach(obj => {obj.y2 = obj.y1 < height/2 ? obj.y1 - minFracY*obj.distY : obj.y1 + minFracY*obj.distY});
    }
    return arr.map(obj => {return `M${obj.x1} ${obj.y1} L${obj.x2} ${obj.y2}z`});
}

function generateDynamicCircularSvgPathD(peak, index, deltaX, deltaY, radius, deg, absMax, height, width) {
    let x = Math.cos(deg * index - Math.PI / 2);
    let y = Math.sin(deg * index - Math.PI / 2);
    let h = radius + Math.round(peak / absMax * height / 2);
    if (isNaN(h)) {
        h = 0;
    }
    let x1 = deltaX + radius * x;
    let y1 = deltaY + radius * y;
    let x2 = deltaX + x * h;
    let y2 = deltaY + y * h;
    let distX = Math.abs(x1 - x2);
    let distY = Math.abs(y1 - y2);
    let distToPerimeterX = x1 < width/2 ? x1 : width - x1;
    let distToPerimeterY = y1 < height/2 ? y1 : height - y1;
    return {
        x1: deltaX + radius * x,
        y1: deltaY + radius * y,
        x2: deltaX + x * h,
        y2: deltaY + y * h,
        distX: distX,
        distY: distY,
        distToPerimeterX: distToPerimeterX,
        distToPerimeterY: distToPerimeterY,
        fracX: distToPerimeterX/distX,
        fracY: distToPerimeterY/distY
    };
}

export function getChannelPeaks(peaks) {
    let length = ~~(peaks.length / 2);
    let channelPeaks = [];
    for (let i = 0; i < length; i++) {
        channelPeaks.push(peaks[i]);
    }
    return channelPeaks;
}

export function getLeftChannelPeaks(peaks) {
    let leftChannelPeaks = [];
    for (let i = 0; i < peaks.length; i++) {
        if (i % 2 === 0) {
            leftChannelPeaks.push(peaks[i]);
        }
    }
    return leftChannelPeaks;
}

export function normalizePeaks(peaks) {
    let max = Math.max(...peaks);
    let min = Math.min(...peaks);
    let absmax = -min > max ? -min : max;
    return absmax || 1;
}

/**
 * Find and return the cursor angle from the center of an SVG
 * @param el    {Node}  The svg node to track movement on
 * @returns {number}
 */
export function getCursorLocationAngle(el) {
    if (!isEmpty(el)) {
        let bbox = el.getBBox();
        let svg;
        d3.select('#studioPlayGridSvg').each(function () { svg = this });
        let pt = svg.createSVGPoint();
        pt.x = bbox.x + bbox.width / 2;
        pt.y = bbox.y + bbox.height / 2;
        let center = pt.matrixTransform(el.getScreenCTM());
        let diffX = center.x - d3.event.clientX;
        let diffY = center.y - d3.event.clientY;
        let radians = Math.atan2(diffY, diffX);
        let angle = (radians * (180 / Math.PI)) - 90;
        return (360 + angle) % 360;
    }
}

/**
 * calculate the angle between cursor event location to the middle of width & height
 * @param e - e.target should be the SVG clicked
 * @param width - svg viewbox width
 * @param height - svg viewbox height
 * @returns {number}
 */
export function getEventLocationAngle(e, width, height) {
    const pt = e.target.createSVGPoint();

    let cx = e.targetTouches ? e.targetTouches[0].clientX : e.clientX;
    let cy = e.targetTouches ? e.targetTouches[0].clientY : e.clientY;

    pt.x = cx;
    pt.y = cy;
    const cursorpt = pt.matrixTransform(e.target.getScreenCTM().inverse());
    cx = cursorpt.x;
    cy = cursorpt.y;

    const mx = width / 2;
    const my = height / 2;

    const coe = cx < mx ? 1 : -1;
    const beyond = cx < mx ? 0.5 : 0;
    const theta = Math.acos(coe * (cy - my) / (Math.sqrt(Math.pow(cx - mx, 2) + Math.pow(cy - my, 2))));
    return beyond + theta / (Math.PI * 2);
}

export function convertSvgToPng(svgElement, canvasElement) {
    return new Promise((resolve, reject) => {
        let svgString = new XMLSerializer().serializeToString(svgElement);

        let ctx = canvasElement.getContext("2d");

        let img = new Image();
        let svg = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
        let url = URL.createObjectURL(svg);
        img.onload = async () => {
            ctx.drawImage(img, 0, 0);
            let pngUrl = canvasElement.toDataURL("image/png");
            URL.revokeObjectURL(url);
            resolve(btoa(pngUrl.split(',')[1]));
        };

        img.onerror = err => {
            reject(err);
        };

        img.src = url;

    });

}
